/**
 * TasksSection Component
 * Container component that fetches tasks and handles Continue/Dismiss actions
 * Design based on Figma specifications
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@nextui-org/react";
import { CheckCircle } from "lucide-react";

import { getAgentSession } from "@/modules/sessions/requests";
import { handleContinueMultiVendorAssessment } from "@/modules/sessions/use-cases/agent-session.use-case";
import { addNotification } from "@/shared/states/notification";
import { ROUTES } from "@/shared/constants/routes";
import type { MultiVendorAgentSession } from "@/modules/sessions/types";

import { TaskItem } from "./TaskItem";
import { SectionError } from "./SectionError";
import { useGetTasks } from "../requests/getTasks";
import { useDismissTask } from "../requests/dismissTask";
import type { Task } from "../types";

/**
 * Fixed container height from Figma: 640px
 * Header: 20px + gap 16px = 36px
 * Padding: 16px top + 16px bottom = 32px
 * Available for scroll: 640 - 36 - 32 = 572px
 */
const CONTAINER_HEIGHT = "h-[640px]";
const SCROLL_AREA_HEIGHT = "max-h-[572px]";

/**
 * Loading skeleton for tasks section
 */
const TasksSectionSkeleton = () => (
  <div
    className={`flex flex-col items-start p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg ${CONTAINER_HEIGHT}`}
  >
    <Skeleton className="h-5 w-16 rounded flex-shrink-0" />
    <div className={`flex flex-col gap-2 w-full overflow-y-auto ${SCROLL_AREA_HEIGHT}`}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
        <Skeleton key={i} className="h-11 w-full rounded-lg flex-shrink-0" />
      ))}
    </div>
  </div>
);

/**
 * Empty state when no tasks
 */
const TasksSectionEmpty = () => (
  <div
    className={`flex flex-col items-start p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg ${CONTAINER_HEIGHT}`}
  >
    <h3 className="text-sm font-medium leading-5 text-[#0F172B] flex-shrink-0">Tasks</h3>
    <div className="flex flex-col items-center justify-center flex-grow w-full">
      <CheckCircle className="w-12 h-12 mb-2 text-success" />
      <p className="text-sm font-medium text-gray-500">No pending tasks</p>
      <p className="text-xs text-gray-400">All caught up!</p>
    </div>
  </div>
);

/**
 * Props for TasksSection component
 */
interface TasksSectionProps {
  showAll?: boolean;
}

/**
 * Main TasksSection component
 */
export function TasksSection({ showAll = false }: TasksSectionProps) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetTasks(showAll);
  const { mutate: dismissTask } = useDismissTask();

  // Per-task loading state for continue action
  const [loadingTasks, setLoadingTasks] = useState<Record<string, boolean>>({});

  /**
   * Handle continue action - navigate to agent session
   * Following pattern from sessions page handleContinueSession
   */
  const handleContinueTask = useCallback(
    async (task: Task) => {
      // Prevent duplicate clicks
      if (loadingTasks[task.id]) return;

      setLoadingTasks((prev) => ({ ...prev, [task.id]: true }));

      try {
        // Fetch full session with steps
        const response = await getAgentSession(task.agent_session_id);

        // Load into agent store using existing use-case
        await handleContinueMultiVendorAssessment({
          session: response.data.session as MultiVendorAgentSession,
          steps: response.data.steps,
        });

        // Navigate to agent workflow page
        navigate(`/${ROUTES.AGENT}/${task.agent_session_id}`);
      } catch (err) {
        console.error("Error loading session:", err);
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to load session. Please try again.",
        });
      } finally {
        setLoadingTasks((prev) => ({ ...prev, [task.id]: false }));
      }
    },
    [loadingTasks, navigate, addNotification],
  );

  /**
   * Handle dismiss action - remove task with optimistic update
   */
  const handleDismissTask = useCallback(
    (taskId: string) => {
      dismissTask(taskId);
    },
    [dismissTask],
  );

  // Loading state
  if (isLoading) {
    return <TasksSectionSkeleton />;
  }

  // Error state
  if (isError || !data) {
    return (
      <div
        className={`flex flex-col items-start p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg ${CONTAINER_HEIGHT}`}
      >
        <h3 className="text-sm font-medium leading-5 text-[#0F172B] flex-shrink-0">Tasks</h3>
        <div className="flex-grow flex items-center justify-center w-full">
          <SectionError
            title="Failed to load tasks"
            description="Unable to fetch task data"
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  // Empty state
  if (!data.tasks || data.tasks.length === 0) {
    return <TasksSectionEmpty />;
  }

  return (
    <div
      className={`flex flex-col items-start p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg ${CONTAINER_HEIGHT}`}
    >
      {/* Header */}
      <h3 className="text-sm font-medium leading-5 text-[#0F172B] flex-shrink-0">Tasks</h3>

      {/* Task List with Scroll */}
      <div className={`flex flex-col gap-2 w-full overflow-y-auto ${SCROLL_AREA_HEIGHT} pr-1`}>
        {data.tasks.map((task) => (
          <TaskItem
            key={task.id}
            task={task}
            isLoading={loadingTasks[task.id] || false}
            onContinue={handleContinueTask}
            onDismiss={handleDismissTask}
          />
        ))}
      </div>
    </div>
  );
}

export default TasksSection;
